/**
 * Dialog for configuring module action settings. Renders plugin-specific
 * configuration forms dynamically based on the selected action type.
 */
import { getFrontendPlugin } from "@unios/action-plugins/frontend";
import { useState, useEffect, useMemo, Suspense } from "react";


import { ActionTypeSelector } from "./ActionTypeSelector";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";


export interface ActionTypeInfo {
	id: string;
	slug: string;
	name: string;
	type: string;
	completionType: string;
	defaultConfig?: Record<string, unknown> | null;
}

export interface ModuleActionInfo {
	id: string;
	moduleId: string;
	actionTypeId: string;
	name: string;
	description: string | null;
	config: Record<string, unknown> | null;
	order: number;
	required: boolean;
	actionType: ActionTypeInfo | null;
}

interface ActionConfigDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	moduleId: string;
	action: ModuleActionInfo | null; // null for create mode
	onSuccess: () => void;
}

/**
 * Dialog for creating or editing a module action.
 * @param props - component props
 * @param props.open - whether the dialog is open
 * @param props.onOpenChange - callback when open state changes
 * @param props.moduleId - the module ID to add/edit action for
 * @param props.action - the action to edit (null for create mode)
 * @param props.onSuccess - callback when save succeeds
 */
export function ActionConfigDialog({ open, onOpenChange, moduleId, action, onSuccess }: ActionConfigDialogProps) {
	const isEditing = !!action;

	// Form state
	const [actionTypeId, setActionTypeId] = useState<string>("");
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [required, setRequired] = useState(true);
	const [config, setConfig] = useState<Record<string, unknown>>({});

	// Get action types for default config
	const actionTypesQuery = trpc.actions.listActionTypes.useQuery();
	const actionTypes = useMemo(() => actionTypesQuery.data ?? [], [actionTypesQuery.data]);

	// Get the selected action type
	const selectedActionType = useMemo(() => {
		if (actionTypeId) {
			return actionTypes.find((at) => at.id === actionTypeId);
		}
		if (action?.actionTypeId) {
			return actionTypes.find((at) => at.id === action.actionTypeId);
		}
		return undefined;
	}, [actionTypeId, actionTypes, action?.actionTypeId]);

	// Get the plugin's ConfigEditor component
	const pluginComponents = useMemo(() => {
		if (selectedActionType?.slug) {
			return getFrontendPlugin(selectedActionType.slug);
		}
		return undefined;
	}, [selectedActionType?.slug]);

	// Reset form when dialog opens
	useEffect(() => {
		if (open) {
			if (action) {
				// Edit mode
				setActionTypeId(action.actionTypeId);
				setName(action.name);
				setDescription(action.description ?? "");
				setRequired(action.required);
				setConfig(action.config ?? action.actionType?.defaultConfig ?? {});
			}
			else {
				// Create mode
				setActionTypeId("");
				setName("");
				setDescription("");
				setRequired(true);
				setConfig({});
			}
		}
	}, [open, action]);

	// Auto-fill name and config from action type when creating
	useEffect(() => {
		if (!isEditing && actionTypeId) {
			const selectedType = actionTypes.find((at) => at.id === actionTypeId);
			if (selectedType) {
				if (!name) {
					setName(selectedType.name);
				}
				// Set default config when action type changes
				setConfig(selectedType.defaultConfig ?? {});
			}
		}
	}, [actionTypeId, actionTypes, isEditing, name]);

	const createMutation = trpc.actions.createModuleAction.useMutation({
		onSuccess: () => {
			onSuccess();
			onOpenChange(false);
		},
	});

	const updateMutation = trpc.actions.updateModuleAction.useMutation({
		onSuccess: () => {
			onSuccess();
			onOpenChange(false);
		},
	});

	const isPending = createMutation.isPending || updateMutation.isPending;

	/**
	 * Handles form submission for creating or updating an action
	 */
	const handleSubmit = () => {
		if (isEditing) {
			updateMutation.mutate({
				actionId: action.id,
				name,
				description: description ? description : null,
				required,
				config,
			});
		}
		else {
			createMutation.mutate({
				moduleId,
				actionTypeId,
				name,
				description: description ? description : null,
				config,
				required,
			});
		}
	};

	const canSubmit = (isEditing || actionTypeId) && name.trim();

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>{isEditing ? "Edit Action" : "Add Action"}</DialogTitle>
					<DialogDescription>
						{isEditing
							? "Update the action configuration."
							: "Add a new action to this module's workflow."}
					</DialogDescription>
				</DialogHeader>

				<form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
					<div className="space-y-4 py-4">
						{!isEditing && (
							<div className="space-y-2">
								<Label>Action Type</Label>
								<ActionTypeSelector value={actionTypeId} onValueChange={setActionTypeId} />
							</div>
						)}

						<div className="space-y-2">
							<Label htmlFor="action-name">Name</Label>
							<Input
								id="action-name"
								placeholder="Action name"
								value={name}
								onChange={(e) => { setName(e.target.value); }}
							/>
						</div>

						<div className="space-y-2">
							<Label htmlFor="action-description">Description (optional)</Label>
							<Input
								id="action-description"
								placeholder="Brief description of this action"
								value={description}
								onChange={(e) => { setDescription(e.target.value); }}
							/>
						</div>

						<div className="flex items-center gap-2">
							<Checkbox
								id="action-required"
								checked={required}
								onCheckedChange={(checked: boolean) => { setRequired(checked); }}
							/>
							<Label htmlFor="action-required">Required action</Label>
						</div>

						{/* Plugin ConfigEditor */}
						{pluginComponents?.ConfigEditor && (
							<div className="border-t pt-4 mt-4">
								<Label className="mb-2 block">Action Configuration</Label>
								<Suspense fallback={<div className="text-sm text-muted-foreground">Loading configuration editor...</div>}>
									<pluginComponents.ConfigEditor
										value={config}
										onChange={(value) => { setConfig(value as Record<string, unknown>); }}
										disabled={isPending}
									/>
								</Suspense>
							</div>
						)}
					</div>

					{(createMutation.error ?? updateMutation.error) && (
						<div className="text-sm text-destructive">
							{createMutation.error?.message ?? updateMutation.error?.message}
						</div>
					)}

					<DialogFooter>
						<Button variant="outline" type="button" onClick={() => { onOpenChange(false); }}>
							Cancel
						</Button>
						<Button type="submit" disabled={!canSubmit || isPending}>
							{isPending ? "Saving..." : isEditing ? "Save Changes" : "Add Action"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
